import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Agriculture as AgricultureIcon } from '@mui/icons-material';
import { RecordItem } from '../components/zones/BusinessMapTab';
import { effectiveRate, formatRate, isBanded } from '../utils/bandRate';
import FieldSeasonTable, { FieldSeasonItem } from '../components/zones/FieldSeasonTable';
import RecordEditDialog from '../components/admin/RecordEditDialog';

interface Option {
  id: string;
  name: string;
}

const formatHa = (n: number | null | undefined) =>
  n == null ? '—' : n.toLocaleString('en-AU', { maximumFractionDigits: 2 });

// Admin: one farm or field in one place — its field seasons (the business
// page's "Other Records") and every record on it, for a season or all of
// them. Opened in a new tab from RecordEditDialog's "Field view" link with
// ?businessId=&farmId=&fieldId=&timePeriodId=&recordId= (recordId is the
// record it was opened from, highlighted). The filters live in the URL, so
// a reload or a copied link keeps them. Double-click a record to edit it.
const FieldView: React.FC = () => {
  const auth = useAuth();
  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${auth.tokens?.accessToken}` }), [auth.tokens?.accessToken]);
  const [params, setParams] = useSearchParams();
  const businessId = params.get('businessId');
  const farmId = params.get('farmId');
  const fieldId = params.get('fieldId');
  const timePeriodId = params.get('timePeriodId') ?? '';
  const recordId = params.get('recordId');
  // A field, or its whole farm (every field's records and seasons, and
  // records on the farm itself).
  const scope: 'field' | 'farm' = fieldId && (params.get('scope') !== 'farm' || !farmId) ? 'field' : 'farm';

  const setParam = (key: string, value: string | null) =>
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value); else next.delete(key);
      return next;
    }, { replace: true });

  const [businessName, setBusinessName] = useState('');
  const [farmName, setFarmName] = useState('');
  const [fieldName, setFieldName] = useState('');
  const [seasons, setSeasons] = useState<Option[]>([]);

  const [records, setRecords] = useState<RecordItem[]>([]);
  const [recordCount, setRecordCount] = useState(0);
  const [recordPage, setRecordPage] = useState(0);
  const [recordPageSize, setRecordPageSize] = useState(100);
  const [fieldSeasons, setFieldSeasons] = useState<FieldSeasonItem[]>([]);
  const [fieldSeasonCount, setFieldSeasonCount] = useState(0);
  const [fieldSeasonPage, setFieldSeasonPage] = useState(0);
  const [fieldSeasonPageSize, setFieldSeasonPageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const highlightRef = useRef<HTMLTableRowElement | null>(null);
  const scrolledTo = useRef<string | null>(null);

  useEffect(() => {
    if (!auth.tokens?.accessToken || !businessId) return;
    const name = (url: string, set: (n: string) => void) =>
      fetch(url, { headers: authHeaders() })
        .then((res) => (res.ok ? res.json() : null))
        .then((body) => body && set(body.name ?? ''))
        .catch(() => undefined);
    name(`/api/businesses/${businessId}`, setBusinessName);
    if (farmId) name(`/api/spatial-features/${farmId}`, setFarmName);
    if (fieldId) name(`/api/spatial-features/${fieldId}`, setFieldName);
    fetch('/api/time-periods', { headers: authHeaders() })
      .then((res) => (res.ok ? res.json() : []))
      .then((list) => setSeasons(list.map((t: any) => ({ id: t.id, name: t.name }))))
      .catch(() => setSeasons([]));
  }, [businessId, farmId, fieldId, auth.tokens?.accessToken, authHeaders]);

  const load = useCallback(async () => {
    const featureId = scope === 'field' ? fieldId : farmId;
    if (!businessId || !featureId) return;
    setLoading(true);
    setError(null);
    try {
      const recordParams = new URLSearchParams({
        spatialFeatureId: featureId,
        includeDescendants: 'true',
        page: String(recordPage + 1),
        pageSize: String(recordPageSize),
        sortDir: 'asc',
      });
      const seasonParams = new URLSearchParams({ page: String(fieldSeasonPage + 1), pageSize: String(fieldSeasonPageSize) });
      if (scope === 'field') seasonParams.set('fieldId', featureId); else seasonParams.set('farmId', featureId);
      if (timePeriodId) {
        recordParams.set('timePeriodId', timePeriodId);
        seasonParams.set('timePeriodId', timePeriodId);
      }
      const [recordRes, seasonRes] = await Promise.all([
        fetch(`/api/businesses/${businessId}/records?${recordParams}`, { headers: authHeaders() }),
        fetch(`/api/businesses/${businessId}/field-seasons?${seasonParams}`, { headers: authHeaders() }),
      ]);
      if (!recordRes.ok || !seasonRes.ok) throw new Error('Failed to load this field');
      const recordData = await recordRes.json();
      const seasonData = await seasonRes.json();
      setRecords(recordData.items);
      setRecordCount(recordData.totalCount);
      setFieldSeasons(seasonData.items);
      setFieldSeasonCount(seasonData.totalCount);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [businessId, farmId, fieldId, scope, timePeriodId, recordPage, recordPageSize, fieldSeasonPage, fieldSeasonPageSize, authHeaders]);

  useEffect(() => {
    if (auth.tokens?.accessToken) load();
  }, [load, auth.tokens?.accessToken]);

  // Bring the record this was opened from into view, once.
  useEffect(() => {
    if (recordId && scrolledTo.current !== recordId && highlightRef.current) {
      highlightRef.current.scrollIntoView({ block: 'center' });
      scrolledTo.current = recordId;
    }
  }, [records, recordId]);

  useEffect(() => {
    document.title = `${[farmName, fieldName].filter(Boolean).join(' / ') || 'Field view'} — DMOTrak`;
  }, [farmName, fieldName]);

  if (!businessId || (!farmId && !fieldId)) {
    return <Alert severity="info">Open the field view from a record (Edit Record → Field view).</Alert>;
  }

  const showField = scope === 'farm';
  const showSeason = !timePeriodId;

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <Box>
        <Typography variant="overline" color="text.secondary">Field view{businessName ? ` · ${businessName}` : ''}</Typography>
        <Typography variant="h5" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AgricultureIcon />
          {[farmName, scope === 'field' ? fieldName : null].filter(Boolean).join(' / ') || '…'}
        </Typography>
      </Box>

      <Box display="flex" flexWrap="wrap" alignItems="center" gap={2}>
        {fieldId && farmId && (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={scope}
            onChange={(_, value) => {
              if (!value) return;
              setRecordPage(0);
              setFieldSeasonPage(0);
              setParam('scope', value === 'farm' ? 'farm' : null);
            }}
          >
            <ToggleButton value="field">Field {fieldName}</ToggleButton>
            <ToggleButton value="farm">Whole farm</ToggleButton>
          </ToggleButtonGroup>
        )}
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="field-view-season">Season</InputLabel>
          <Select
            labelId="field-view-season"
            label="Season"
            value={seasons.some((s) => s.id === timePeriodId) ? timePeriodId : ''}
            onChange={(e) => {
              setRecordPage(0);
              setFieldSeasonPage(0);
              setParam('timePeriodId', e.target.value || null);
            }}
          >
            <MenuItem value=""><em>All seasons</em></MenuItem>
            {seasons.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
          </Select>
        </FormControl>
        {loading && <CircularProgress size={20} />}
      </Box>

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      <Box>
        <Typography variant="h6" gutterBottom>
          Field seasons <Typography component="span" color="text.secondary">({fieldSeasonCount}) — the business page's Other Records</Typography>
        </Typography>
        {fieldSeasonCount === 0 && !loading ? (
          <Typography variant="body2" color="text.secondary">No field season {timePeriodId ? 'for this season' : 'yet'}.</Typography>
        ) : (
          <FieldSeasonTable
            items={fieldSeasons}
            totalCount={fieldSeasonCount}
            page={fieldSeasonPage}
            pageSize={fieldSeasonPageSize}
            onPageChange={setFieldSeasonPage}
            onPageSizeChange={(size) => { setFieldSeasonPageSize(size); setFieldSeasonPage(0); }}
            showBusiness={false}
          />
        )}
      </Box>

      <Box>
        <Typography variant="h6" gutterBottom>
          Records <Typography component="span" color="text.secondary">({recordCount}) — oldest first; double-click one to edit it</Typography>
        </Typography>
        {recordCount === 0 && !loading ? (
          <Typography variant="body2" color="text.secondary">No records {timePeriodId ? 'in this season' : 'yet'}.</Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.5, fontSize: '0.8rem' }, '& .MuiTableCell-head': { fontWeight: 600, whiteSpace: 'nowrap' } }}>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  {showSeason && <TableCell>Season</TableCell>}
                  {showField && <TableCell>Field</TableCell>}
                  <TableCell>Operation</TableCell>
                  <TableCell>Products</TableCell>
                  <TableCell>Gene stack</TableCell>
                  <TableCell align="right">Area (ha)</TableCell>
                  <TableCell>Irrigation</TableCell>
                  <TableCell>Region</TableCell>
                  <TableCell>Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {records.map((r) => (
                  <TableRow
                    key={r.id}
                    hover
                    selected={r.id === recordId}
                    ref={r.id === recordId ? highlightRef : undefined}
                    title="Double-click to edit"
                    sx={{ cursor: 'pointer' }}
                    onDoubleClick={() => setEditId(r.id)}
                  >
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(r.timeFrom).toLocaleDateString('en-AU')}</TableCell>
                    {showSeason && <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.timePeriodName ?? '—'}</TableCell>}
                    {showField && (
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {r.fieldName ?? <Typography variant="inherit" color="text.secondary">On the farm</Typography>}
                      </TableCell>
                    )}
                    <TableCell>
                      {r.operationTypeName}
                      {r.operationMethodName && <Typography variant="caption" color="text.secondary" display="block">{r.operationMethodName}</Typography>}
                    </TableCell>
                    <TableCell sx={{ minWidth: 240 }}>
                      {r.products.length === 0
                        ? '—'
                        : r.products.map((p, i) => (
                            <Box key={i}>{p.productName} <Typography component="span" variant="inherit" color="text.secondary">
                              @ {p.rate} {p.rateUnitName}
                              {isBanded(r.bandPercent) && `, ${r.bandPercent}% band = ${formatRate(effectiveRate(p.rate, r.bandPercent))} effective`}
                            </Typography></Box>
                          ))}
                    </TableCell>
                    <TableCell>{r.geneStackName ?? '—'}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {formatHa(r.effectiveAreaHectares)}
                      {isBanded(r.bandPercent) && r.affectedAreaHectares != null && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ whiteSpace: 'nowrap' }}>
                          {formatHa(r.affectedAreaHectares)} treated
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{r.irrigationTypeName ?? '—'}</TableCell>
                    <TableCell>{r.linkedRegionName ?? r.regionName ?? '—'}</TableCell>
                    <TableCell sx={{ maxWidth: 260 }}>{r.notes ?? ''}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={recordCount}
              page={recordPage}
              rowsPerPage={recordPageSize}
              rowsPerPageOptions={[50, 100, 250]}
              onPageChange={(_, p) => setRecordPage(p)}
              onRowsPerPageChange={(e) => { setRecordPageSize(Number(e.target.value)); setRecordPage(0); }}
            />
          </TableContainer>
        )}
      </Box>

      <RecordEditDialog
        open={!!editId}
        businessId={businessId}
        recordId={editId}
        onClose={() => setEditId(null)}
        onSaved={load}
      />
    </Box>
  );
};

export default FieldView;
